import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import api from '../utils/api';
import { Btn, Badge, Toggle, Modal, Input, Select, Loading, EmptyState } from '../components/ui';

function SkillBar({ pct }) {
  const color = pct >= 80 ? '#639922' : pct >= 60 ? '#EF9F27' : '#E24B4A';
  return <div style={{ height: 4, borderRadius: 2, marginTop: 3, width: `${Math.min(pct, 100)}%`, background: color }} />;
}

function EmpCard({ emp, lines, onUpdate }) {
  const [transferOpen, setTransferOpen] = useState(false);
  const [addSkillOpen, setAddSkillOpen] = useState(false);
  const [empOperations, setEmpOperations] = useState([]);
  const [newLine, setNewLine] = useState('');
  const [newOp, setNewOp] = useState('');
  const [newMachine, setNewMachine] = useState('');
  const [newCT, setNewCT] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null); // composite key: `${operation_id}__${machine_type}`
  const [editCT, setEditCT] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const skillKey = (sk) => `${sk.operation_id}__${sk.machine_type || ''}`;

  const handleToggle = async () => {
    try { await api.put(`/employees/${emp.id}`, { ...emp, is_active: !emp.is_active, shift_line_id: emp.shift_line_id }); onUpdate(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${emp.name} permanently? This cannot be undone.`)) return;
    try { await api.delete(`/employees/${emp.id}`); onUpdate(); }
    catch (e) { alert(e.response?.data?.error || 'Error removing employee'); }
  };

  const handleTransfer = async () => {
    if (!newLine) return;
    setSaving(true);
    try {
      await api.post(`/employees/${emp.id}/transfer`, { shift_line_id: newLine });
      setTransferOpen(false); setNewLine(''); onUpdate();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    setSaving(false);
  };

  const selectedNewOp = empOperations.find(o => String(o.id) === String(newOp));
  const newOpIsMachine = selectedNewOp && selectedNewOp.op_type !== 'Non-machine';

  const handleAddSkill = async () => {
    if (!newOp || !newCT) return;
    if (newOpIsMachine && !newMachine) return;
    setSaving(true);
    try {
      await api.put(`/employees/${emp.id}/skills`, {
        operation_id: parseInt(newOp),
        machine_type: newOpIsMachine ? newMachine : undefined,
        cycle_time: parseFloat(newCT)
      });
      setAddSkillOpen(false); setNewOp(''); setNewMachine(''); setNewCT(''); onUpdate();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    setSaving(false);
  };

  const handleEditSkill = async (sk) => {
    if (!editCT || parseFloat(editCT) <= 0) return;
    setEditSaving(true);
    try {
      await api.put(`/employees/${emp.id}/skills`, {
        operation_id: sk.operation_id,
        machine_type: sk.machine_type || undefined,
        cycle_time: parseFloat(editCT)
      });
      setEditingSkill(null); setEditCT(''); onUpdate();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    setEditSaving(false);
  };

  const handleDeleteSkill = async (sk) => {
    if (!window.confirm('Remove this skill?')) return;
    try {
      await api.delete(`/employees/${emp.id}/skills/${sk.operation_id}`, { params: { machine_type: sk.machine_type || '' } });
      onUpdate();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const skills = emp.skills || [];

  return (
    <>
      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 9, marginBottom: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px auto auto 50px 70px auto', alignItems: 'center', padding: '10px 14px', gap: 8, background: 'var(--bg-secondary)', borderBottom: '0.5px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{emp.name}</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>{emp.employee_id}</div>
          </div>
          <Badge type={emp.emp_type === 'Operator' ? 'blue' : 'gray'}>{emp.emp_type}</Badge>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-primary)' }}>{emp.factory_name || '—'}</span>
            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{emp.division_name || ''}</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Line {emp.line_number || '—'}</span>
          <span style={{ fontSize: 12 }}>{skills.length} ops</span>
          <Badge type={emp.is_active ? 'green' : 'red'}>{emp.is_active ? 'Active' : 'Inactive'}</Badge>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Toggle value={emp.is_active} onChange={handleToggle} />
            <Btn size="sm" onClick={() => { setNewLine(String(emp.shift_line_id || '')); setTransferOpen(true); }}>Transfer</Btn>
            <Btn size="sm" variant="primary" onClick={async () => {
              try {
                const { data } = await api.get(`/employees/${emp.id}/operations`);
                setEmpOperations(data);
              } catch (e) { setEmpOperations([]); }
              setNewOp(''); setNewMachine(''); setNewCT('');
              setAddSkillOpen(true);
            }}>+ Skill</Btn>
            <Btn size="sm" onClick={handleRemove} style={{ color: 'var(--red)', borderColor: '#F09595' }}>Remove</Btn>
          </div>
        </div>

        {skills.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ padding: '5px 14px', textAlign: 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)' }}>Operation</th>
                <th style={{ padding: '5px 14px', textAlign: 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)', width: 90 }}>Machine</th>
                <th style={{ padding: '5px 14px', textAlign: 'right', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)', width: 80 }}>SAM</th>
                <th style={{ padding: '5px 14px', textAlign: 'right', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)', width: 120 }}>Cycle time</th>
                <th style={{ padding: '5px 14px', textAlign: 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)', width: 130 }}>Skill%</th>
                <th style={{ padding: '5px 14px', textAlign: 'center', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border)', width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {skills.map((sk, i) => {
                const pct = sk.cycle_time > 0 ? Math.min(Math.round(parseFloat(sk.sam) / parseFloat(sk.cycle_time) * 100), 100) : null;
                const bdType = !pct ? 'gray' : pct >= 80 ? 'green' : pct >= 60 ? 'amber' : 'red';
                const isEditing = editingSkill === skillKey(sk);
                return (
                  <tr key={skillKey(sk)} style={{ borderBottom: i < skills.length - 1 ? '0.5px solid var(--border)' : 'none', background: isEditing ? 'var(--acc-bg)' : '' }}>
                    <td style={{ padding: '7px 14px' }}>{sk.operation_name}</td>
                    <td style={{ padding: '7px 14px', color: 'var(--text-secondary)' }}>{sk.machine_type || '—'}</td>
                    <td style={{ padding: '7px 14px', textAlign: 'right', color: 'var(--text-secondary)' }}>{parseFloat(sk.sam).toFixed(2)}</td>
                    <td style={{ padding: '7px 14px', textAlign: 'right' }}>
                      {isEditing ? (
                        <input
                          type="number" value={editCT} onChange={e => setEditCT(e.target.value)} autoFocus
                          style={{ width: 80, padding: '3px 6px', fontSize: 12, border: '0.5px solid var(--acc)', borderRadius: 5, textAlign: 'right', fontFamily: 'inherit', background: '#fff' }}
                          onKeyDown={e => { if (e.key === 'Enter') handleEditSkill(sk); if (e.key === 'Escape') { setEditingSkill(null); setEditCT(''); } }}
                        />
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>{sk.cycle_time ? parseFloat(sk.cycle_time).toFixed(2) : '—'}</span>
                      )}
                    </td>
                    <td style={{ padding: '7px 14px' }}>
                      <Badge type={bdType}>{pct !== null ? `${pct}%` : '—'}</Badge>
                      {pct !== null && <SkillBar pct={pct} />}
                    </td>
                    <td style={{ padding: '7px 14px', textAlign: 'center' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <Btn size="sm" variant="primary" onClick={() => handleEditSkill(sk)} disabled={editSaving}>{editSaving ? '…' : 'Save'}</Btn>
                          <Btn size="sm" onClick={() => { setEditingSkill(null); setEditCT(''); }}>Cancel</Btn>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }}
                            onClick={() => { setEditingSkill(skillKey(sk)); setEditCT(sk.cycle_time ? parseFloat(sk.cycle_time).toFixed(2) : ''); }}>Edit</a>
                          <span style={{ color: 'var(--border-md)' }}>·</span>
                          <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleDeleteSkill(sk)}>Remove</a>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-tertiary)' }}>No skills recorded yet — click + Skill to add</div>
        )}
      </div>

      <Modal open={transferOpen} onClose={() => setTransferOpen(false)} title={`Transfer — ${emp.name}`} width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Current line: <strong style={{ color: 'var(--text-primary)' }}>Line {emp.line_number || '—'}</strong></div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Transfer to line</div>
            <Select value={newLine} onChange={setNewLine} placeholder="Select new line…"
              options={lines.filter(l => String(l.id) !== String(emp.shift_line_id)).map(l => ({ value: l.id, label: `Line ${l.line_number} — ${l.shift_name} (${l.factory_name})` }))}
              style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setTransferOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleTransfer} disabled={saving || !newLine}>{saving ? 'Transferring…' : 'Confirm transfer'}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={addSkillOpen} onClose={() => setAddSkillOpen(false)} title={`Add skill — ${emp.name}`} width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Operation</div>
            <Select value={newOp} onChange={v => { setNewOp(v); setNewMachine(''); }} placeholder="Select operation…"
              options={empOperations.map(o => ({ value: o.id, label: o.name }))}
              style={{ width: '100%' }} />
          </div>
          {newOpIsMachine && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Machine type</div>
              <Select value={newMachine} onChange={setNewMachine} placeholder="Select machine…"
                options={(selectedNewOp?.machine_sams || []).map(m => ({ value: m.machine_type, label: `${m.machine_type} (SAM ${parseFloat(m.sam).toFixed(2)})` }))}
                style={{ width: '100%' }} />
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Cycle time (min)</div>
            <Input value={newCT} onChange={setNewCT} type="number" placeholder="e.g. 1.45" style={{ width: '100%' }} />
          </div>
          {newOp && newCT && parseFloat(newCT) > 0 && (!newOpIsMachine || newMachine) && (() => {
            const effectiveSam = newOpIsMachine
              ? parseFloat((selectedNewOp?.machine_sams || []).find(m => m.machine_type === newMachine)?.sam || 0)
              : parseFloat(selectedNewOp?.sam || 0);
            const pct = effectiveSam ? Math.min(Math.round(effectiveSam / parseFloat(newCT) * 100), 100) : null;
            const color = pct >= 80 ? 'var(--grn)' : pct >= 60 ? 'var(--amb)' : 'var(--red)';
            return pct !== null ? (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 7, padding: '7px 10px', fontSize: 12 }}>
                Skill%: <strong style={{ color }}>{pct}%</strong>
              </div>
            ) : null;
          })()}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setAddSkillOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleAddSkill} disabled={saving || (newOpIsMachine && !newMachine)}>Save</Btn>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default function EmployeeMaster() {
  const [employees, setEmployees] = useState([]);
  const [operations, setOperations] = useState([]); // kept for template download only
  const [lines, setLines] = useState([]);
  const [factories, setFactories] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]); // for template dropdown only
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFactory, setFilterFactory] = useState('');
  const [filterLine, setFilterLine] = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [filterType, setFilterType] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: '', name: '', shift_line_id: '', emp_type: 'Operator' });
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef();

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Employee master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterLine) params.shift_line_id = filterLine;
      if (filterType) params.emp_type = filterType;
      if (filterFactory) params.factory_id = filterFactory;
      if (filterShift) params.shift_name = filterShift;
      const [emps, ops, lns, facts, machines] = await Promise.all([
        api.get('/employees', { params }),
        api.get('/operations'),
        api.get('/shift-lines', { params: filterFactory ? { factory_id: filterFactory } : {} }),
        api.get('/factories'),
        api.get('/machine-types'),
      ]);
      setEmployees(emps.data);
      setOperations(ops.data);
      setLines(lns.data);
      setFactories(facts.data);
      setMachineTypes([...new Set(machines.data.map(m => m.machine_type))].sort());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, filterLine, filterType, filterFactory, filterShift]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredLines = filterFactory
    ? lines.filter(l => String(l.factory_id) === String(filterFactory))
    : lines;

  // Distinct shift names available across all lines, for the Shift filter dropdown
  const shiftOptions = [...new Set(lines.map(l => l.shift_name).filter(Boolean))]
    .sort()
    .map(s => ({ value: s, label: s }));

  const handleAdd = async () => {
    if (!form.employee_id || !form.name) { alert('Employee ID and name are required'); return; }
    try {
      await api.post('/employees', form);
      setAddOpen(false);
      setForm({ employee_id: '', name: '', shift_line_id: '', emp_type: 'Operator' });
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const downloadTemplate = async () => {
    const opNames = operations.map(o => o.name);
    const unitNames = factories.map(f => f.name);
    const lineNumbers = [...new Set(lines.map(l => String(l.line_number)))];
    const typeValues = ['Operator', 'Helper'];
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Employees');
    ws.columns = [
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Employee ID', key: 'emp_id', width: 14 },
      { header: 'Type', key: 'type', width: 12 },
      { header: 'Unit', key: 'unit', width: 16 },
      { header: 'Line', key: 'line', width: 8 },
      { header: 'Operation', key: 'operation', width: 28 },
      { header: 'Machine Type', key: 'machine', width: 16 },
      { header: 'Cycle Time (min)', key: 'ct', width: 16 },
    ];
    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF185FA5' } };
    ws.addRow(['Rama Devi', 'EMP-001', 'Operator', unitNames[0] || 'Unit 1', lineNumbers[0] || '1', opNames[0] || 'Attach collar', machineTypes[0] || 'OL', 1.45]);
    ws.addRow(['Sunita K.', 'EMP-002', 'Operator', unitNames[0] || 'Unit 1', lineNumbers[0] || '1', opNames[1] || 'Bottom hem', machineTypes[0] || 'OL', 1.31]);
    const typeRef = `"${typeValues.join(',')}"`;
    const unitRef = unitNames.length ? `"${unitNames.join(',')}"` : '"Unit 1"';
    const lineRef = lineNumbers.length ? `"${lineNumbers.join(',')}"` : '"1"';
    const machineRef = machineTypes.length ? `"${machineTypes.join(',')}"` : '"—"';
    for (let row = 2; row <= 1000; row++) {
      ws.getCell(`C${row}`).dataValidation = { type: 'list', allowBlank: false, formulae: [typeRef], showErrorMessage: true, errorStyle: 'stop', errorTitle: 'Invalid', error: 'Select Operator or Helper' };
      if (unitNames.length) ws.getCell(`D${row}`).dataValidation = { type: 'list', allowBlank: true, formulae: [unitRef], showErrorMessage: true, errorStyle: 'stop', errorTitle: 'Invalid', error: 'Select a valid unit' };
      if (lineNumbers.length) ws.getCell(`E${row}`).dataValidation = { type: 'list', allowBlank: true, formulae: [lineRef], showErrorMessage: true, errorStyle: 'stop', errorTitle: 'Invalid', error: 'Select a valid line' };
      ws.getCell(`G${row}`).dataValidation = { type: 'list', allowBlank: true, formulae: [machineRef], showErrorMessage: true, errorStyle: 'stop', errorTitle: 'Invalid', error: 'Select a valid machine type, or leave blank for Non-machine operations' };
    }
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'employee_upload_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    try {
      const res = await api.get('/employees/download', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'employees.xlsx'; a.click();
    } catch (e) { alert('Download failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadMsg('Uploading…');
    try {
      const { data } = await api.post('/employees/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadMsg(`✓ Inserted ${data.inserted} records${data.errors?.length ? ` · ${data.errors.length} skipped` : ''}`);
      fetchAll();
    } catch (e) { setUploadMsg('Upload failed: ' + (e.response?.data?.error || e.message)); }
    e.target.value = '';
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 500 }}>Employee master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{employees.length} records</span></div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Operators and helpers with skill profiles</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="inp" placeholder="Search name / ID / operation…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ flex: 1, maxWidth: 240 }} />
        <Select value={filterFactory}
          onChange={v => { setFilterFactory(v); setFilterLine(''); }}
          placeholder="All units"
          options={factories.map(f => ({ value: f.id, label: f.name }))} />
        <Select value={filterLine} onChange={setFilterLine} placeholder="All lines"
          options={filteredLines.map(l => ({ value: l.id, label: `Line ${l.line_number}` }))} />
        <Select value={filterShift} onChange={setFilterShift} placeholder="All shifts"
          options={shiftOptions} />
        <Select value={filterType} onChange={setFilterType} placeholder="All types"
          options={[{ value: 'Operator', label: 'Operator' }, { value: 'Helper', label: 'Helper' }]} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Btn size="sm" onClick={downloadTemplate}>Download template</Btn>
          <Btn size="sm" onClick={() => fileRef.current.click()}>Upload Excel</Btn>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleUpload} />
          <Btn size="sm" onClick={downloadAll}>Download all</Btn>
          <Btn size="sm" variant="primary" onClick={() => setAddOpen(true)}>+ Add new</Btn>
        </div>
      </div>

      {uploadMsg && (
        <div style={{ marginBottom: 10, fontSize: 12, padding: '6px 12px', borderRadius: 7, background: uploadMsg.startsWith('✓') ? 'var(--grn-bg)' : 'var(--red-bg)', color: uploadMsg.startsWith('✓') ? 'var(--grn)' : 'var(--red)' }}>
          {uploadMsg}
        </div>
      )}

      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 11.5, color: 'var(--text-secondary)' }}>
        <strong style={{ color: 'var(--text-primary)' }}>Excel format:</strong>&nbsp;
        {['Name', 'Employee ID', 'Type', 'Unit', 'Line', 'Operation', 'Machine Type', 'Cycle Time'].map((c, i) => (
          <span key={i}><code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>{c}</code>{i < 7 ? ' · ' : ''}</span>
        ))}
        &nbsp;— One row per skill per employee. Leave Machine Type blank for Non-machine operations.
      </div>

      {loading ? <Loading /> : employees.length === 0
        ? <EmptyState message="No employees found" action={<Btn variant="primary" onClick={() => setAddOpen(true)}>+ Add employee</Btn>} />
        : employees.map(emp => (
          <EmpCard key={emp.id} emp={emp} lines={lines} onUpdate={fetchAll} />
        ))
      }

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add employee" width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'Employee ID', key: 'employee_id', placeholder: 'EMP-001' },
            { label: 'Full name', key: 'name', placeholder: 'Rama Devi' },
          ].map(f => (
            <div key={f.key}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{f.label}</div>
              <Input value={form[f.key]} onChange={v => setForm(p => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} style={{ width: '100%' }} />
            </div>
          ))}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Home line</div>
            <Select value={form.shift_line_id} onChange={v => setForm(p => ({ ...p, shift_line_id: v }))}
              placeholder="Select line…"
              options={lines.map(l => ({ value: l.id, label: `Line ${l.line_number} — ${l.shift_name} (${l.factory_name})` }))}
              style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Type</div>
            <Select value={form.emp_type} onChange={v => setForm(p => ({ ...p, emp_type: v }))}
              options={[{ value: 'Operator', label: 'Operator' }, { value: 'Helper', label: 'Helper' }]}
              style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setAddOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleAdd}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}